/**
 * Every server path the web app knows about, in one file.
 *
 * Phase 0's API surface is exactly the authentication set below. Nothing else is
 * called over the network yet — the dashboard hooks resolve locally and are
 * marked as mock boundaries — so when the API lands, this file is the only place
 * that has to agree with it.
 */
/**
 * The API's Nest controllers are declared as `@Controller('auth')`, so this
 * prefix only holds if the API sets a matching global prefix. It is one constant
 * on purpose — see NOTES-FOR-INTEGRATOR.md §2.
 */
export const API_PREFIX = '/api/v1'

export const endpoints = {
  /** POST { email, password } → { accessToken, refreshToken, user } */
  login: `${API_PREFIX}/auth/login`,
  /** POST { refreshToken } → { accessToken, refreshToken } — rotates the pair. */
  refresh: `${API_PREFIX}/auth/refresh`,
  /** POST — revokes the presented refresh family. */
  logout: `${API_PREFIX}/auth/logout`,
  /** GET → AuthenticatedUser for the bearer token. */
  me: `${API_PREFIX}/auth/me`,

  /** GET → the live fleet, served from the Redis projection and never from `positions`. */
  liveFleet: `${API_PREFIX}/live/vehicles`,
  /** GET → one vehicle's projected state. */
  liveVehicle: (id: string) => `${API_PREFIX}/live/vehicles/${id}`,
  /** GET ?lat=&lon=&limit= → a ranking that labels its straight-line fallback. */
  liveClosest: `${API_PREFIX}/live/closest`,
  /** GET → Paginated<VehicleView> */
  vehicles: `${API_PREFIX}/vehicles`,
  /** GET → one vehicle's identity and registry row. */
  vehicle: (id: string) => `${API_PREFIX}/vehicles/${id}`,
  /** GET ?locale= → latest value per channel, including unmapped `io_<avlId>` keys. */
  vehicleLatestTelemetry: (id: string) => `${API_PREFIX}/vehicles/${id}/telemetry/latest`,
  /** GET → what this vehicle can actually report, and why it cannot report the rest. */
  vehicleCapabilities: (id: string) => `${API_PREFIX}/vehicles/${id}/capabilities`,
  /** GET → position history. The only surface in the app that reads the hypertable. */
  vehiclePositions: (id: string) => `${API_PREFIX}/vehicles/${id}/positions`,
  fleets: `${API_PREFIX}/fleets`,
} as const
